/**
 * Category Tabs - Sticky horizontal tab navigation for deal categories
 */

import { useRef, useEffect, useState } from 'react';
import { Clock, Users, Tag, Package, LayoutGrid, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { DealCategory, DealsSummary } from '../types';

interface Props {
  activeCategory: DealCategory;
  onChange: (category: DealCategory) => void;
  summary?: DealsSummary;
  sticky?: boolean;
}

const CATEGORY_CONFIG: Record<
  DealCategory,
  { icon: React.ComponentType<{ className?: string }>; labelKey: string; fallback: string }
> = {
  all: { icon: LayoutGrid, labelKey: 'deals.tabs.all', fallback: 'All Deals' },
  last_chance: { icon: Clock, labelKey: 'deals.tabs.lastChance', fallback: 'Last Chance' },
  family_offers: { icon: Users, labelKey: 'deals.tabs.family', fallback: 'Family Offers' },
  limited_time: { icon: Tag, labelKey: 'deals.tabs.limited', fallback: 'Limited Time' },
  as_is: { icon: Package, labelKey: 'deals.tabs.asIs', fallback: 'As-Is' },
};

const CATEGORY_ORDER: DealCategory[] = ['all', 'last_chance', 'family_offers', 'limited_time', 'as_is'];

export default function CategoryTabs({ activeCategory, onChange, summary, sticky = true }: Props) {
  const { t } = useLanguage();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Check scroll position for arrow visibility
  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 200;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const getCategoryCount = (category: DealCategory): number | undefined => {
    if (!summary) return undefined;
    switch (category) {
      case 'all':
        return (
          summary.last_chance.count +
          summary.family_offers.count +
          summary.limited_time.count +
          summary.as_is.count
        );
      case 'last_chance':
        return summary.last_chance.count;
      case 'family_offers':
        return summary.family_offers.count;
      case 'limited_time':
        return summary.limited_time.count;
      case 'as_is':
        return summary.as_is.count;
      default:
        return undefined;
    }
  };

  return (
    <div
      className={`bg-white border-b border-gray-100 ${
        sticky ? 'sticky top-0 z-30' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative">
          {/* Left Arrow */}
          {showLeftArrow && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
              aria-label={t('categoryTabs.scrollLeft', 'Scroll left')}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          {/* Tabs Container */}
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex overflow-x-auto scrollbar-hide py-2 -mx-4 px-4 sm:mx-0 sm:px-0 gap-1"
          >
            {CATEGORY_ORDER.map((category) => {
              const config = CATEGORY_CONFIG[category];
              const Icon = config.icon;
              const count = getCategoryCount(category);
              const isActive = activeCategory === category;

              return (
                <button
                  key={category}
                  onClick={() => onChange(category)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm whitespace-nowrap transition-all duration-200 ${
                    isActive
                      ? 'bg-primary-100 text-primary-700 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-primary-600' : 'text-gray-400'}`} />
                  <span>{t(config.labelKey, config.fallback)}</span>
                  {count !== undefined && (
                    <span
                      className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                        isActive
                          ? 'bg-primary-200 text-primary-800'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Right Arrow */}
          {showRightArrow && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
              aria-label={t('categoryTabs.scrollRight', 'Scroll right')}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollbar Hide CSS */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
